import { Seccion, useSecciones } from "@/components/shell/Seccion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AMORTIZACIONES,
  BASES_MORA,
  CALCULOS_INTERES,
  ETIQUETA_PERIODO,
  METODOS_COMISION,
  METODOS_PRELACION,
  APLICACIONES_PREPAGO,
  metodoPrelacionDe,
  aplicacionPrepagoDe,
  type MetodoPrelacion,
  type AplicacionPrepago,
  MONEDAS,
  PERIODICIDADES_PAGO,
  TIPOS_CREDITO,
  TIPOS_OPERACION,
  TIPOS_TASA,
  fmtMXN,
  fmtPct,
  tasaAnualEfectiva,
  type Amortizacion,
  type BaseMora,
  baseMoraDe,
  FORMULA_MORA,
  type CalculoInteres,
  type MetodoComision,
  type Moneda,
  type ParamsCredito,
  type PeriodicidadPago,
  type TipoCredito,
  type TipoOperacion,
  type TipoTasa,
} from "@/lib/operativo";

type SeccionId = "credito" | "tasas" | "mora" | "opciones";

interface Props {
  idPrefijo: string;
  params: ParamsCredito;
  onChange: (patch: Partial<ParamsCredito>) => void;
  /** Campos 3 y 5 (número de pagos y monto) solo se muestran en la solicitud. */
  plazo?: { valor: number; onChange: (n: number) => void };
  monto?: { valor: number; onChange: (n: number) => void };
}

export function ParametrosCredito({ idPrefijo, params: p, onChange, plazo, monto }: Props) {
  const id = (k: string) => `${idPrefijo}-${k}`;
  const anual = tasaAnualEfectiva(p);
  const secs = useSecciones<SeccionId>(["credito", "tasas", "mora", "opciones"]);

  return (
    <div className="space-y-3">
      <Seccion
        titulo="Datos del crédito"
        abierta={secs.abierta("credito")}
        onToggle={() => secs.toggle("credito")}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* 1 */}
          <div className="space-y-1.5">
            <Label>1. Tipo de crédito</Label>
            <Select
              value={p.tipoCredito}
              onValueChange={(v) => onChange({ tipoCredito: v as TipoCredito })}
            >
              <SelectTrigger id={id("tipoCredito")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_CREDITO.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {p.tipoCredito === "CRÉDITO REVOLVENTE" && (
              <p className="text-[11px] text-muted-foreground">
                La operación de la línea (cortes, disposiciones y pago mínimo) se realiza en la
                subsección “Línea Revolvente”.
              </p>
            )}
          </div>
          {/* 2 */}
          <div className="space-y-1.5">
            <Label>2. Tipo de pago (periodicidad)</Label>
            <Select
              value={p.tipoPago}
              onValueChange={(v) => onChange({ tipoPago: v as PeriodicidadPago })}
            >
              <SelectTrigger id={id("tipoPago")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODICIDADES_PAGO.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* 3 */}
          {plazo && (
            <div className="space-y-1.5">
              <Label htmlFor={id("plazo")}>3. Número de pagos (plazo)</Label>
              <Input
                id={id("plazo")}
                type="number"
                inputMode="decimal"
                min={1}
                className="num"
                value={Number.isNaN(plazo.valor) ? "" : plazo.valor}
                onChange={(e) => plazo.onChange(e.target.valueAsNumber)}
              />
            </div>
          )}
          {/* 4 */}
          <div className="space-y-1.5">
            <Label>4. Moneda</Label>
            <Select value={p.moneda} onValueChange={(v) => onChange({ moneda: v as Moneda })}>
              <SelectTrigger id={id("moneda")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONEDAS.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m === "MXN" ? "Pesos Mexicanos (MXN)" : "UDIS"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {p.moneda === "UDIS" && (
            <div className="space-y-1.5">
              <Label htmlFor={id("valorUdi")}>Valor de la UDI (MXN)</Label>
              <Input
                id={id("valorUdi")}
                type="number"
                inputMode="decimal"
                step={0.0001}
                className="num"
                value={Number.isNaN(p.valorUdi) ? "" : p.valorUdi}
                onChange={(e) => onChange({ valorUdi: e.target.valueAsNumber })}
              />
            </div>
          )}
          {/* 5 */}
          {monto && (
            <div className="space-y-1.5">
              <Label htmlFor={id("monto")}>5. Monto solicitado ({p.moneda})</Label>
              <Input
                id={id("monto")}
                type="number"
                inputMode="decimal"
                step={100}
                className="num"
                value={Number.isNaN(monto.valor) ? "" : monto.valor}
                onChange={(e) => monto.onChange(e.target.valueAsNumber)}
              />
              {p.moneda === "UDIS" && (
                <p className="text-xs text-muted-foreground num">
                  Equivale a {fmtMXN((monto.valor || 0) * (p.valorUdi || 0))}
                </p>
              )}
            </div>
          )}
          {/* 6 */}
          <div className="space-y-1.5">
            <Label>6. Tipo de operación</Label>
            <Select
              value={p.tipoOperacion}
              onValueChange={(v) => onChange({ tipoOperacion: v as TipoOperacion })}
            >
              <SelectTrigger id={id("tipoOperacion")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_OPERACION.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Seccion>

      <Seccion
        titulo="Tasas e intereses"
        abierta={secs.abierta("tasas")}
        onToggle={() => secs.toggle("tasas")}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* 7 */}
          <div className="space-y-1.5">
            <Label>7. Tipo de tasa</Label>
            <Select value={p.tipoTasa} onValueChange={(v) => onChange({ tipoTasa: v as TipoTasa })}>
              <SelectTrigger id={id("tipoTasa")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_TASA.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {p.tipoTasa === "VARIABLE" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor={id("ref")}>Tasa de referencia (TIIE, %)</Label>
                <Input
                  id={id("ref")}
                  type="number"
                  inputMode="decimal"
                  step={0.01}
                  className="num"
                  value={Number.isNaN(p.tasaReferencia) ? "" : p.tasaReferencia}
                  onChange={(e) => onChange({ tasaReferencia: e.target.valueAsNumber })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={id("spread")}>Spread (puntos)</Label>
                <Input
                  id={id("spread")}
                  type="number"
                  inputMode="decimal"
                  step={0.01}
                  className="num"
                  value={Number.isNaN(p.spread) ? "" : p.spread}
                  onChange={(e) => onChange({ spread: e.target.valueAsNumber })}
                />
              </div>
            </>
          )}
          {/* 8 */}
          <div className="space-y-1.5">
            <Label>8. Amortización de capital</Label>
            <Select
              value={p.amortizacion}
              onValueChange={(v) => onChange({ amortizacion: v as Amortizacion })}
            >
              <SelectTrigger id={id("amortizacion")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AMORTIZACIONES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* 9 */}
          <div className="space-y-1.5">
            <Label>9. Tipo de cálculo de interés</Label>
            <Select
              value={p.calculoInteres}
              onValueChange={(v) => onChange({ calculoInteres: v as CalculoInteres })}
            >
              <SelectTrigger id={id("calculoInteres")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CALCULOS_INTERES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* 10 */}
          <div className="space-y-1.5">
            <Label htmlFor={id("tasaAnual")}>10. Tasa anual (%)</Label>
            <Input
              id={id("tasaAnual")}
              type="number"
              inputMode="decimal"
              step={0.01}
              className="num"
              disabled={p.tipoTasa === "VARIABLE"}
              value={Number.isNaN(p.tasaAnual) ? "" : p.tasaAnual}
              onChange={(e) => onChange({ tasaAnual: e.target.valueAsNumber })}
            />
            <p className="text-xs text-muted-foreground num">
              {fmtPct(anual)} anual ·{" "}
              {fmtPct(
                anual /
                  (p.tipoPago === "MENSUAL"
                    ? 12
                    : p.tipoPago === "QUINCENAL"
                      ? 24
                      : p.tipoPago === "CATORCENAL"
                        ? 360 / 14
                        : 52),
              )}{" "}
              {ETIQUETA_PERIODO[p.tipoPago]}
            </p>
          </div>
          {/* 10b */}
          <div className="space-y-1.5">
            <Label>10b. Método de comisión financiada</Label>
            <Select
              value={p.metodoComisionFinanciada ?? "Suma directa"}
              onValueChange={(v) => onChange({ metodoComisionFinanciada: v as MetodoComision })}
            >
              <SelectTrigger id={id("metodoComision")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METODOS_COMISION.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {(p.metodoComisionFinanciada ?? "Suma directa") === "Suma directa"
                ? "Monto financiado = monto + Σ cargos financiados (con IVA)."
                : "Monto financiado = (monto + gastos financiados) / (1 − % comisión × IVA)."}
            </p>
          </div>
          {/* 10c */}
          <div className="space-y-1.5">
            <Label>10c. Método de aplicación dentro de la prelación</Label>
            <Select
              value={metodoPrelacionDe(p)}
              onValueChange={(v) => onChange({ metodoPrelacion: v as MetodoPrelacion })}
            >
              <SelectTrigger id={id("metodoPrelacion")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METODOS_PRELACION.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {metodoPrelacionDe(p) === "Secuencial estricto"
                ? "El dinero agota cada nivel antes de pasar al siguiente: IVA pena → pena → IVA mora → mora → IVA interés → interés → capital."
                : "Reparto proporcional 1:0.16 entre cada IVA y su concepto base (esquema original del Excel)."}
            </p>
          </div>
          {/* 10d */}
          <div className="space-y-1.5">
            <Label>10d. Aplicación de prepagos</Label>
            <Select
              value={aplicacionPrepagoDe(p)}
              onValueChange={(v) => onChange({ aplicacionPrepago: v as AplicacionPrepago })}
            >
              <SelectTrigger id={id("aplicacionPrepago")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APLICACIONES_PREPAGO.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {aplicacionPrepagoDe(p) === "Reducir cuota"
                ? "Cada abono extra a capital re-amortiza la cuota de los pagos restantes sobre el nuevo saldo."
                : "Se conserva la cuota y el calendario se acorta desde el final. Requiere convenio por escrito con el acreditado."}
            </p>
          </div>
        </div>
      </Seccion>

      <Seccion
        titulo="Mora y cobranza"
        abierta={secs.abierta("mora")}
        onToggle={() => secs.toggle("mora")}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* 11 */}
          <div className="space-y-1.5">
            <Label htmlFor={id("tasaMoratoria")}>11. Tasa moratoria (%)</Label>
            <Input
              id={id("tasaMoratoria")}
              type="number"
              inputMode="decimal"
              step={0.01}
              className="num"
              value={Number.isNaN(p.tasaMoratoria) ? "" : p.tasaMoratoria}
              onChange={(e) => onChange({ tasaMoratoria: e.target.valueAsNumber })}
            />
          </div>
          {/* 11b */}
          <div className="space-y-1.5">
            <Label>11b. Base de mora</Label>
            <Select
              value={baseMoraDe(p)}
              onValueChange={(v) => onChange({ baseMora: v as BaseMora })}
            >
              <SelectTrigger id={id("baseMora")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BASES_MORA.map((b) => (
                  <SelectItem key={b} value={b}>
                    {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {baseMoraDe(p) === "Saldo insoluto"
                ? "Esquema machote: mora sobre el saldo insoluto inicial del período."
                : FORMULA_MORA + ". El IVA de la cuota no forma parte de la base."}
            </p>
          </div>
          {/* 11c */}
          <div className="space-y-1.5">
            <Label htmlFor={id("diasGraciaMora")}>11c. Días de gracia de mora</Label>
            <Input
              id={id("diasGraciaMora")}
              type="number"
              inputMode="decimal"
              min={0}
              className="num"
              value={Number.isFinite(p.diasGraciaMora as number) ? (p.diasGraciaMora as number) : 0}
              onChange={(e) =>
                onChange({ diasGraciaMora: Math.max(0, e.target.valueAsNumber || 0) })
              }
            />
            <p className="text-xs text-muted-foreground">
              {(p.diasGraciaMora ?? 0) === 0
                ? "0 días: la mora corre desde el día siguiente a la fecha límite de pago."
                : `La mora inicia después de ${p.diasGraciaMora} día(s) de tolerancia.`}
            </p>
          </div>
          {/* 12 */}
          <div className="space-y-1.5">
            <Label htmlFor={id("numImpagos")}>12. Número de impagos (cartera vencida)</Label>
            <Input
              id={id("numImpagos")}
              type="number"
              inputMode="decimal"
              min={1}
              className="num"
              value={Number.isNaN(p.numImpagos) ? "" : p.numImpagos}
              onChange={(e) => onChange({ numImpagos: e.target.valueAsNumber })}
            />
          </div>
          {/* 13 */}
          <div className="space-y-1.5">
            <Label htmlFor={id("periodosGracia")}>13. Período de gracia (solo interés)</Label>
            <Input
              id={id("periodosGracia")}
              type="number"
              inputMode="decimal"
              min={0}
              className="num"
              value={Number.isNaN(p.periodosGracia) ? "" : p.periodosGracia}
              onChange={(e) => onChange({ periodosGracia: e.target.valueAsNumber })}
            />
            <p className="text-xs text-muted-foreground">
              Solo interés: períodos iniciales sin amortizar capital.
            </p>
          </div>
        </div>
      </Seccion>

      <Seccion
        titulo="Opciones del producto"
        abierta={secs.abierta("opciones")}
        onToggle={() => secs.toggle("opciones")}
      >
        <div className="grid gap-3 rounded-md bg-surface px-4 py-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex items-start gap-2">
            <Switch
              id={id("iva")}
              checked={p.ivaEnCuota}
              onCheckedChange={(v) => onChange({ ivaEnCuota: v })}
            />
            <Label htmlFor={id("iva")} className="text-xs leading-snug">
              Incluir IVA de interés en la cuota (esquema Meta Patrimonio)
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <Switch
              id={id("ivaInteres")}
              checked={p.ivaInteresOrdinario ?? true}
              onCheckedChange={(v) => onChange({ ivaInteresOrdinario: v })}
            />
            <Label htmlFor={id("ivaInteres")} className="text-xs leading-snug">
              ¿Se calcula IVA al interés ordinario? (No: persona sin actividad que causa IVA)
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <Switch
              id={id("ivaMora")}
              checked={p.ivaMoraActiva ?? true}
              onCheckedChange={(v) => onChange({ ivaMoraActiva: v })}
            />
            <Label htmlFor={id("ivaMora")} className="text-xs leading-snug">
              ¿Causa IVA la mora y la pena convencional?
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <Switch
              id={id("anticipado")}
              checked={p.permitePagoAnticipado}
              onCheckedChange={(v) => onChange({ permitePagoAnticipado: v })}
            />
            <Label htmlFor={id("anticipado")} className="text-xs leading-snug">
              Permite pago anticipado
            </Label>
          </div>
          <div className="flex items-start gap-2">
            <Switch
              id={id("penal")}
              checked={p.penalidadPrepago}
              onCheckedChange={(v) => onChange({ penalidadPrepago: v })}
            />
            <Label htmlFor={id("penal")} className="text-xs leading-snug">
              Penalidad por pago anticipado
            </Label>
          </div>
          {p.penalidadPrepago && (
            <div className="space-y-1.5">
              <Label htmlFor={id("penalPct")} className="text-xs">
                % sobre capital prepagado
              </Label>
              <Input
                id={id("penalPct")}
                type="number"
                inputMode="decimal"
                step={0.1}
                className="h-9 num"
                value={Number.isNaN(p.penalidadPrepagoPct) ? "" : p.penalidadPrepagoPct}
                onChange={(e) => onChange({ penalidadPrepagoPct: e.target.valueAsNumber })}
              />
            </div>
          )}
          <div className="flex items-start gap-2">
            <Switch
              id={id("aval")}
              checked={p.requiereAval}
              onCheckedChange={(v) => onChange({ requiereAval: v })}
            />
            <Label htmlFor={id("aval")} className="text-xs leading-snug">
              Requiere aval / obligado solidario
            </Label>
          </div>
        </div>
      </Seccion>
    </div>
  );
}
